import React, { useState, useCallback, useEffect } from "react";
import {
    Edit,
    SimpleForm,
    TextInput,
    Loading,
    Toolbar,
    DeleteButton,
    BooleanInput,
    useInput,
    FormDataConsumer,
} from "react-admin";
import Grid from "@material-ui/core/Grid";
import WithProps from "../Custom/WithProps";
import { CustomEditSave } from "../Custom/create-save-button";
import { makeStyles } from "@material-ui/core/styles";
import {
    validateDomain,
    validateRequired,
    validateNumericRequired,
} from "../../utils/Validations";
import "./index.css";
import CustomStoreSelect from "./CustomStoreSelect";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { apiUrl } from "../../dataProvider";
import {
    Card,
    CardContent,
    Collapse,
    Typography,
    IconButton,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const useStyles = makeStyles({
    capitalize: {
        textTransform: "capitalize",
        fontWeight: "bold",
    },
    container: {
        width: "100%",
    },
    password: {
        margin: "10px 0",
        minHeight: "50px",
    },
    marginBounds: {
        marginTop: "20px",
    },
    formIterator: {
        "& ul": {
            width: "80%",
            display: "flex",
            justifyContent: "space-between",
        },
    },
    restrictionsCard: {
        marginTop: "20px",
        width: "100%",
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        cursor: "pointer",
    },
    cardContent: {
        paddingTop: "0",
    },
    restrictionField: {
        marginBottom: "16px",
    },
});

// Multiselect component for restrictions
const RestrictionMultiSelect = ({ source, label, resource, classes }) => {
    const { input } = useInput({ source });
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [idField, setIdField] = useState('id'); // Track which ID field to use

    // Helper function to extract ID from an item, trying multiple field names
    const getIdFromItem = useCallback((item) => {
        if (!item) return null;
        
        // Helper to check if a value is a valid ID
        const isValidId = (value) => {
            return value !== null && value !== undefined && value !== '' && 
                   (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''));
        };
        
        // Try common ID field names in order of preference
        const idFields = [
            'id',  'machine_pattern_id', 'width_id', 'weight_id',
            'gemstone_id', 'gemstone_arrangement_id', 'inlay_id',
            'mga_color_option_id', 'machine_pattern_color_option_id', 'cerakote_color_id',
            'engraving_option_id', 'finish_id', 'profile_id'
        ];
        
        for (const field of idFields) {
            const value = item[field];
            if (isValidId(value)) {
                return value;
            }
        }
        
        return null;
    }, []);

    const getOptions = useCallback(async (params) => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiUrl}/${resource}`, { params });
            let data = response.data?.data || response.data || [];
            
            // Ensure data is an array
            if (!Array.isArray(data)) {
                data = [];
            }
            
            // Determine which ID field to use by checking the first item
            if (data.length > 0) {
                const firstItem = data[0];
                if (firstItem.id !== undefined && firstItem.id !== null) {
                    setIdField('id');
                } else if (firstItem.sku_code !== undefined && firstItem.sku_code !== null) {
                    setIdField('sku_code');
                } else {
                    // Try to find any field ending with _id that has a value
                    const idFieldFound = Object.keys(firstItem).find(key => 
                        key.endsWith('_id') && firstItem[key] !== null && firstItem[key] !== undefined
                    );
                    if (idFieldFound) {
                        setIdField(idFieldFound);
                    }
                }
            }
            
            // Filter out items without valid IDs to prevent selection issues
            const validData = data.filter(item => {
                const id = getIdFromItem(item);
                return id !== null && id !== undefined && id !== '';
            });
            
            setList(validData);
            setLoading(false);
            return validData;
        } catch (error) {
            setLoading(false);
            console.error(`Error fetching ${resource}:`, error);
            return [];
        }
    }, [resource, getIdFromItem]);

    useEffect(() => {
        getOptions({
            'page[number]': 1,
            'page[size]': 1000,
        });
    }, [getOptions]);

    const filterOptions = (value) => {
        if (!value) return list;
        const searchTerm = value.toLowerCase();
        return list.filter(
            (item) =>
                item.name?.toLowerCase().includes(searchTerm) ||
                item.sku_code?.toLowerCase().includes(searchTerm)
        );
    };

    const getSearchOptions = (value, callback) => {
        setTimeout(() => {
            callback(filterOptions(value));
        }, 300);
    };

    const handleChange = (selectedOptions) => {
        if (selectedOptions && Array.isArray(selectedOptions)) {
            // Extract IDs from selected options using the detected ID field
            const selectedIds = selectedOptions.map((item) => {
                const id = getIdFromItem(item);
                if (id === null || id === undefined || id === '') return null;
                // Store as number if it's a valid number, otherwise as string
                const numId = Number(id);
                return !isNaN(numId) && isFinite(numId) ? numId : String(id);
            }).filter(id => id !== null && id !== '');
            
            // Remove duplicates
            const uniqueIds = [...new Set(selectedIds)];
            input.onChange(uniqueIds);
        } else {
            input.onChange([]);
        }
    };

    // Get selected values from IDs - returns full option objects for react-select
    const getSelectedValues = useCallback(() => {
        if (!input.value || !Array.isArray(input.value) || list.length === 0) return [];
        
        // Normalize all IDs to strings for consistent comparison
        const normalizeId = (id) => {
            if (id === null || id === undefined || id === '') return null;
            return String(id);
        };
        
        // Create a Set of normalized input value IDs for O(1) lookup
        const inputValueSet = new Set(
            input.value.map(normalizeId).filter(id => id !== null && id !== '')
        );
        
        if (inputValueSet.size === 0) return [];
        
        // Filter list to only include items whose IDs match the input values
        // Return the exact objects from the list to ensure react-select can match them properly
        const selected = list.filter((item) => {
            if (!item) return false;
            const itemId = normalizeId(getIdFromItem(item));
            return itemId !== null && itemId !== '' && inputValueSet.has(itemId);
        });
        
        // Ensure we don't return duplicates (in case API has duplicate IDs)
        const seenIds = new Set();
        return selected.filter((item) => {
            const itemId = normalizeId(getIdFromItem(item));
            if (seenIds.has(itemId)) return false;
            seenIds.add(itemId);
            return true;
        });
    }, [input.value, list, getIdFromItem]);

    const selectedValues = getSelectedValues();

    return (
        <div className={classes.restrictionField}>
            <p className="label">{label}</p>
            <AsyncSelect
                isMulti
                isClearable
                cacheOptions
                defaultOptions={list}
                loadOptions={getSearchOptions}
                isLoading={loading}
                value={selectedValues}
                onChange={handleChange}
                getOptionLabel={(e) => e.name || e.sku_code || String(getIdFromItem(e) || '')}
                getOptionValue={(e) => {
                    const id = getIdFromItem(e);
                    return id !== null ? String(id) : '';
                }}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                styles={{
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
            />
        </div>
    );
};

// Restrictions Card Component
const RestrictionsCard = ({ classes }) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const genericRestrictions = [
        {
            source: "ring_material_restrictions",
            label: "Ring Materials",
            resource: "custom-pricing/ring-materials",
        },
        {
            source: "finish_restrictions",
            label: "Finishes",
            resource: "custom-pricing/finishes",
        },
    ];

    const signetRestrictions = [
        {
            source: "signet_inlay_restrictions",
            label: "Signet Inlays",
            resource: "custom-pricing/signet-inlays",
        },
    ];

    const mensBuilderRestrictions = [
        {
            source: "sleeve_restrictions",
            label: "Sleeves",
            resource: "custom-pricing/sleeves",
        },
        {
            source: "design_pattern_restrictions",
            label: "Design Patterns",
            resource: "custom-pricing/machine-patterns",
        },
        {
            source: "profile_restrictions",
            label: "Profiles",
            resource: "custom-pricing/profiles",
        },
        {
            source: "width_restrictions",
            label: "Width Options",
            resource: "custom-pricing/widths",
        },
        {
            source: "weight_restrictions",
            label: "Weight Options",
            resource: "custom-pricing/weights",
        },
        {
            source: "gemstone_arrangement_restrictions",
            label: "Gemstone Arrangements",
            resource: "custom-pricing/gemstone-arrangements",
        },
        {
            source: "gemstone_restrictions",
            label: "Gemstones",
            resource: "custom-pricing/gemstones",
        },
        {
            source: "inlay_restrictions",
            label: "Inlays",
            resource: "custom-pricing/inlays",
        },
        {
            source: "groove_type_restrictions",
            label: "Inlay Placements",
            resource: "custom-pricing/groove-types",
        },
        {
            source: "machine_pattern_color_option_restrictions",
            label: "Machine Pattern Color Options",
            resource: "custom-pricing/machine-pattern-color-options",
        },
        {
            source: "mga_color_option_restrictions",
            label: "MGA Color Options",
            resource: "custom-pricing/mga-color-options",
        },
        {
            source: "all_cerakote_restrictions",
            label: "All Cerakote",
            resource: "custom-pricing/all-cerakote-options",
        },
        {
            source: "engraving_option_restrictions",
            label: "Engraving Options",
            resource: "custom-pricing/engraving-fonts",
        },
    ];

    return (
        <Card className={classes.restrictionsCard}>
            <div className={classes.cardHeader} onClick={handleExpandClick}>
                <Typography variant="h6">Restrictions</Typography>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </div>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent className={classes.cardContent}>
                    {/* Generic Section */}
                    <Typography variant="h6" style={{ marginBottom: "16px", marginTop: "8px" }}>
                        Generic
                    </Typography>
                    <Grid container spacing={2}>
                        {genericRestrictions.map((restriction) => (
                            <Grid item xs={12} sm={6} key={restriction.source}>
                                <RestrictionMultiSelect
                                    source={restriction.source}
                                    label={restriction.label}
                                    resource={restriction.resource}
                                    classes={classes}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Signet Section */}
                    <Typography variant="h6" style={{ marginBottom: "16px", marginTop: "24px" }}>
                        Signet
                    </Typography>
                    <Grid container spacing={2}>
                        {signetRestrictions.map((restriction) => (
                            <Grid item xs={12} sm={6} key={restriction.source}>
                                <RestrictionMultiSelect
                                    source={restriction.source}
                                    label={restriction.label}
                                    resource={restriction.resource}
                                    classes={classes}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Mens Builder Section */}
                    <Typography variant="h6" style={{ marginBottom: "16px", marginTop: "24px" }}>
                        Mens Builder
                    </Typography>
                    <Grid container spacing={2}>
                        {mensBuilderRestrictions.map((restriction) => (
                            <Grid item xs={12} sm={6} key={restriction.source}>
                                <RestrictionMultiSelect
                                    source={restriction.source}
                                    label={restriction.label}
                                    resource={restriction.resource}
                                    classes={classes}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Collapse>
        </Card>
    );
};

const StoreEditToolbar = (props) => (
    <Toolbar {...props}>
        <CustomEditSave
            redirect="list"
            submitOnEnter={false}
            variant="text"
            pristine={props.pristine}
        />
        {props.delete && <DeleteButton undoable={false} />}
    </Toolbar>
);

export const PlaformSelect = (props) => {
    const { input } = useInput({ source: "platform_name" });
    const options = [
        { value: "shopify", label: "Shopify" },
        { value: "php", label: "PHP" },
        { value: "magento", label: "Magneto" },
        { value: "iframe", label: "Iframe" },
        { value: "wordpress", label: "Wordpress" },
    ];

    const handleChange = (callback) => (selectedopt) => {
        callback(selectedopt?.value);
    };

    return (
        <Select
            {...input}
            options={options}
            onChange={handleChange(input.onChange)}
            value={options.find((item) => item.value === input.value)}
            styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
        />
    );
};

export const ShopifyStoreEdit = (props) => {
    const classes = useStyles();
    const { permissions } = props;

    if (!permissions) return <Loading />;
    const permissionsList = permissions.split(",");

    return (
        <Edit {...props} className="overflow-visible">
            <WithProps>
                {({ record, ...props }) => (
                    <SimpleForm
                        record={record}
                        {...props}
                        undoable={false}
                        toolbar={
                            <StoreEditToolbar
                                delete={permissionsList.includes(
                                    "delete_shopify_stores"
                                )}
                            />
                        }
                    >
                        <Grid
                            container
                            className={classes.container}
                            justify="space-between"
                        >
                            <Grid item xs={5}>
                                <p className="label">Platform Name</p>
                                <PlaformSelect />
                            </Grid>
                            <FormDataConsumer>
                                {({ formData, ...rest }) => {
                                    console.log("formData", formData);
                                    if (formData.platform_name === "magento") {
                                        return (
                                            <>
                                                <Grid item xs={5}>
                                                    <TextInput
                                                        type="number"
                                                        source="attribute_set_id"
                                                        label="Attribute Id"
                                                        validate={
                                                            validateRequired
                                                        }
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={5}>
                                                    <TextInput
                                                        type="number"
                                                        source="category_id"
                                                        label="Category Id"
                                                        fullWidth
                                                    />
                                                </Grid>
                                            </>
                                        );
                                    }
                                }}
                            </FormDataConsumer>
                            <Grid item xs={5}>
                                <TextInput
                                    source="store_name"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    source="keys.api_key"
                                    label="Api key"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    source="keys.password"
                                    label="Password"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    placeholder="weareemblem.myshopify.com/"
                                    source="base_url"
                                    label="Store url"
                                    validate={[
                                        validateRequired,
                                        validateDomain,
                                    ]}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    placeholder="customize"
                                    source="subpath"
                                    label="Subpath"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    placeholder="USD / CAD / GBP"
                                    source="currency"
                                    label="Currency"
                                    validate={validateRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextInput
                                    type="number"
                                    source="buffer_shipping_days"
                                    label="Buffer Shipping Days"
                                    validate={validateNumericRequired}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <BooleanInput source="show_msrp" />
                                <BooleanInput source="is_approved" label="Subscribed" />
                                <BooleanInput source="show_women_builder" />
                                <BooleanInput source="show_signet_builder" />
                                <BooleanInput source="show_collegiate_builder" />
                            </Grid>
                            <Grid item xs={5}>
                                <p className="label">Store</p>
                                <CustomStoreSelect />
                            </Grid>
                        </Grid>
                        <RestrictionsCard classes={classes} />
                    </SimpleForm>
                )}
            </WithProps>
        </Edit>
    );
};
